import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Divider from '@mui/joy/Divider';
import {
  EnvelopeSimple,
  ArrowBendUpLeft as ReplyIcon,
  CaretDown,
  CaretUp,
  Paperclip,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import type { EmailMessage } from '../../../types/inbox';
import { EmailBodyViewer } from '../../inbox/EmailBodyViewer';
import { EmailReplyComposer } from '../../inbox/EmailReplyComposer';

interface EmailThreadTimelineCardProps {
  /** All synced messages of one thread, ascending by sent_at. */
  messages: EmailMessage[];
  canReply: boolean;
  onReplied?: () => void;
}

const messageTime = (message: EmailMessage): string =>
  message.sent_at ? format(new Date(message.sent_at), 'MMM d, h:mm a') : '';

/**
 * One email THREAD on the contact timeline, Close-style: the collapsed card
 * shows the conversation envelope (subject, counterparty, message count);
 * expanding lists every message in order with the latest one open. Bodies
 * load on demand per message via EmailBodyViewer; the thread-level reply
 * form (To/Cc/Subject prefilled, editable) sends through the viewer's own
 * connected mailbox.
 */
export const EmailThreadTimelineCard: React.FC<EmailThreadTimelineCardProps> = ({
  messages,
  canReply,
  onReplied,
}) => {
  const latest = messages[messages.length - 1];
  const [expanded, setExpanded] = useState(false);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set([latest.id]));
  const [replying, setReplying] = useState(false);

  if (messages.length === 0) return null;

  const subject = messages[0].subject || latest.subject || '(no subject)';
  const latestInbound = [...messages].reverse().find(m => m.direction === 'inbound');
  const counterparty = latestInbound
    ? (latestInbound.from_display || latestInbound.from_address)
    : latest.to_addresses.join(', ');
  const hasAttachments = messages.some(m => m.has_attachments);
  const isLatestInbound = latest.direction === 'inbound';

  const defaultReplySubject = /^re:/i.test(subject) ? subject : `Re: ${subject}`;
  const defaultReplyTo = latestInbound
    ? [latestInbound.from_address]
    : latest.to_addresses;

  const toggleMessage = (id: number) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: 'md',
        overflow: 'hidden',
        borderLeft: '3px solid',
        borderLeftColor: isLatestInbound ? 'primary.400' : 'success.400',
      }}
    >
      {/* Thread envelope — always visible, click to expand the conversation */}
      <Box
        onClick={() => setExpanded(v => !v)}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'background.level1' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <EnvelopeSimple size={16} color="var(--joy-palette-text-secondary)" />
          <Typography level="title-sm" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {counterparty}
          </Typography>
          {messages.length > 1 && (
            <Chip size="sm" variant="soft" color="neutral">
              {messages.length} emails
            </Chip>
          )}
          <Chip size="sm" variant="soft" color={isLatestInbound ? 'primary' : 'success'}>
            {isLatestInbound ? 'Received' : 'Sent'}
          </Chip>
          {hasAttachments ? <Paperclip size={14} /> : null}
          <Typography level="body-xs" color="neutral">
            {latest.sent_at ? format(new Date(latest.sent_at), 'h:mm a') : ''}
          </Typography>
          <IconButton size="sm" variant="plain" color="neutral">
            {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </IconButton>
        </Stack>
        <Typography level="body-sm" fontWeight="md" noWrap sx={{ mt: 0.5 }}>
          {subject}
        </Typography>
        {!expanded && (
          <Typography level="body-xs" color="neutral" noWrap>
            {latest.snippet}
          </Typography>
        )}
      </Box>

      {expanded && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Stack spacing={1}>
            {messages.map((message, index) => {
              const isOpen = openIds.has(message.id);
              const isInbound = message.direction === 'inbound';
              return (
                <Box key={message.id}>
                  {index > 0 && <Divider sx={{ mb: 1 }} />}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    onClick={() => toggleMessage(message.id)}
                    sx={{
                      cursor: 'pointer',
                      py: 0.25,
                      '&:hover': { bgcolor: 'background.level1' },
                      borderRadius: 'sm',
                    }}
                  >
                    <Chip size="sm" variant="soft" color={isInbound ? 'primary' : 'success'}>
                      {isInbound ? 'Received' : 'Sent'}
                    </Chip>
                    <Typography level="body-sm" sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {isInbound
                        ? (message.from_display || message.from_address)
                        : `To: ${message.to_addresses.join(', ')}`}
                    </Typography>
                    {message.has_attachments ? <Paperclip size={13} /> : null}
                    <Typography level="body-xs" color="neutral">
                      {messageTime(message)}
                    </Typography>
                    <IconButton size="sm" variant="plain" color="neutral">
                      {isOpen ? <CaretUp size={13} /> : <CaretDown size={13} />}
                    </IconButton>
                  </Stack>
                  {!isOpen && (
                    <Typography level="body-xs" color="neutral" noWrap sx={{ pl: 0.5 }}>
                      {message.snippet}
                    </Typography>
                  )}
                  {isOpen && (
                    <Box sx={{ mt: 0.5 }}>
                      {message.direction === 'outbound' && message.account_email && (
                        <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>
                          Sent from {message.account_email}
                        </Typography>
                      )}
                      <EmailBodyViewer messageId={message.id} />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>

          {canReply && !replying && (
            <Button
              size="sm"
              variant="soft"
              startDecorator={<ReplyIcon size={14} />}
              onClick={() => setReplying(true)}
              sx={{ mt: 1 }}
            >
              Reply
            </Button>
          )}

          {replying && (
            <Box sx={{ mt: 1 }}>
              <EmailReplyComposer
                threadId={latest.thread}
                defaultTo={defaultReplyTo}
                defaultSubject={defaultReplySubject}
                onSent={() => {
                  setReplying(false);
                  onReplied?.();
                }}
                onCancel={() => setReplying(false)}
              />
            </Box>
          )}
        </Box>
      )}
    </Sheet>
  );
};

export default EmailThreadTimelineCard;
